import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Divider, Loading, Typography } from '@guildeducationinc/recess';
import ArrowForwardIcon from '@guildeducationinc/recess/icons/ArrowForwardIcon';
import CloseIcon from '@guildeducationinc/recess/icons/CloseIcon';
import WarningOutlinedIcon from '@guildeducationinc/recess/icons/WarningOutlinedIcon';
import { ActivityInfo } from '@src/components/Employee/ActivityInfo/ActivityInfo';
import { InfoRequestedDialog } from '@src/components/Employee/InfoRequestedDialog/InfoRequestedDialog';
import { useAuth } from '@src/hooks/useAuth';
import { useComments, useCommentsSingleExpense } from '@src/hooks/useComments';
import { useMyBenefits } from '@src/hooks/useMyBenefits';
import { PaymentMethod, PaymentRequestStatus, PreimbursementStatus, ProgramStatus, RuleType, } from '@src/types';
import { getType } from '@src/utilities/activityHelper';
import { getDeadlineErrorMessage, getTermDate, isWithinDocumentDeadline, } from '@src/utilities/paymentRequestHelper';
import { last } from 'lodash';
import { classNames, TrackableButton, useBreakpoints, useLocale, usePreferenceState, } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './RequestNotification.module.scss';
export const RequestNotification = ({ activity, onReviewed, compact, }) => {
    const { currentUser } = useAuth();
    const { t, formatToLocalDatetime, formatToLocalDate } = useLocale();
    const [preference, setPreference, { loading }] = usePreferenceState('requestNotifications', {});
    const navigate = useNavigate();
    const { isMobile } = useBreakpoints();
    const { isProgram } = getType(activity);
    const [showReviewDialog, setShowReviewDialog] = useState(false);
    // Determine if this is a single expense flow
    const isSingleExpense = activity.program.isSingleExpense && activity.program.id && activity.paymentRequest?.id;
    // For single expense flow, query both program and payment request comments
    const singleExpenseComments = useCommentsSingleExpense({
        programId: activity.program.id,
        paymentRequestId: activity.paymentRequest?.id ?? 0,
        isInternal: false,
        skip: !isSingleExpense || !currentUser,
    });
    // For regular flow, query single commentable only if not single expense
    const regularComments = useComments({
        commentableId: activity.activityId,
        commentableType: isProgram ? 'programs' : 'payment_requests',
        isInternal: false,
        skip: Boolean(isSingleExpense) || !currentUser,
    });
    // Use the appropriate query result based on flow type
    const { comments } = isSingleExpense ? singleExpenseComments : regularComments;
    const recentCommentFromApprover = useMemo(() => last(comments.filter(c => c.createdById !== currentUser.id)), [comments, currentUser]);
    const { getPolicy, loading: loadingPolicy } = useMyBenefits({
        program: activity.program,
        paymentRequest: activity.paymentRequest,
    });
    const approvalChainPolicy = getPolicy(RuleType.APPROVAL_CHAIN, activity.program);
    const withinDocumentDeadline = useMemo(() => {
        return activity.paymentRequest
            ? isWithinDocumentDeadline(activity.paymentRequest, approvalChainPolicy, t).success
            : false;
    }, [activity.paymentRequest, approvalChainPolicy]);
    const isProofNeeded = !isProgram &&
        activity.paymentRequest &&
        (activity.paymentRequest.status === PaymentRequestStatus.AWAITING_DOCUMENTS ||
            (activity.paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT &&
                activity.paymentRequest.preimbursementStatus === PreimbursementStatus.PENDING_PROOF));
    const isTermEnded = activity.paymentRequest &&
        activity.paymentRequest.termEndDate &&
        new Date(activity.paymentRequest.termEndDate) < new Date();
    const isInfoNeeded = isProgram
        ? activity.program.status === ProgramStatus.INFO_REQUESTED
        : activity.paymentRequest?.status === PaymentRequestStatus.INFO_REQUESTED;
    const isRejected = isProgram
        ? activity.program.status === ProgramStatus.REJECTED
        : activity.paymentRequest?.status === PaymentRequestStatus.REJECTED ||
            activity.paymentRequest?.status === PaymentRequestStatus.CLOSED ||
            activity.paymentRequest?.status === PaymentRequestStatus.CANCELLED;
    const handleActionClick = () => {
        if (isProofNeeded) {
            navigate(`/payments/${activity.paymentRequest?.id}/edit`);
        }
        else if (isRejected) {
            setPreference({
                ...preference,
                [activity.id]: {
                    dismissed: true,
                },
            });
        }
        else {
            setShowReviewDialog(true);
        }
    };
    const getDocumentDeadlineMessage = () => {
        const paymentRequest = activity.paymentRequest;
        if (!paymentRequest)
            return '';
        const approvalPolicyConfig = paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT
            ? approvalChainPolicy?.preimbursementApproval
            : approvalChainPolicy?.paymentRequestApproval;
        const documentDeadline = approvalPolicyConfig?.documentDeadline || {};
        const startDate = getTermDate(paymentRequest, documentDeadline.minIntervalBasedOn, documentDeadline.minInterval);
        const endDate = getTermDate(paymentRequest, documentDeadline.maxIntervalBasedOn, documentDeadline.maxInterval);
        const [_, params] = getDeadlineErrorMessage(formatToLocalDate, false, documentDeadline, startDate ?? null, endDate ?? null);
        return t('DocumentDeadlineMessage', {
            values: {
                ...params,
                link: (text) => (_jsx(TrackableButton, { btnType: 'link', component: Link, enText: 'Employer policy', tag: 'request_notification', to: '/reimbursement-policy', children: text })),
            },
        });
    };
    if (loading || preference[activity.id]?.dismissed) {
        return null;
    }
    return (_jsx(Loading, { label: '', loaded: !loading && !loadingPolicy, overlay: true, children: _jsxs("div", { className: classNames(styles.container, isInfoNeeded || (isProofNeeded && isTermEnded) ? styles.infoNeeded : '', isProofNeeded && !isTermEnded ? styles.fyi : '', isRejected ? styles.rejected : '', compact ? styles.compact : 'w-full'), children: [_jsxs("div", { className: styles.content, children: [_jsxs("div", { className: styles.title, children: [((isProofNeeded && withinDocumentDeadline) || isInfoNeeded) && (_jsxs(_Fragment, { children: [_jsx(WarningOutlinedIcon, { size: 24 }), ' ', _jsx(Typography, { variant: 'label1', children: "Action required\u00A0-" })] })), _jsx(Typography, { variant: 'label1', children: isInfoNeeded
                                        ? t('Info needed')
                                        : isProofNeeded && withinDocumentDeadline
                                            ? t('Proof of payment and completion needed')
                                            : isRejected
                                                ? t('Rejected')
                                                : t('You are approved! Be sure to complete your term, meet academic requirements and submit proof of payment.') })] }), _jsxs("div", { className: styles.details, children: [!compact && (_jsxs(_Fragment, { children: [_jsx(ActivityInfo, { activity: activity }), _jsx(Divider, { className: classNames(styles.divider), orientation: isMobile ? 'horizontal' : 'vertical', opacity: '2' })] })), _jsxs("div", { className: 'flex flex-col align-middle', children: [recentCommentFromApprover && (_jsxs("div", { className: classNames(styles.comment), children: [_jsxs("div", { className: 'flex gap-2', children: [_jsx(Typography, { weight: '700', children: t('Commented on') }), _jsx(Typography, { className: styles.commentDate, children: formatToLocalDatetime(recentCommentFromApprover.createdAt, {
                                                                dateStyle: 'medium',
                                                            }) })] }), recentCommentFromApprover && (_jsx(Typography, { children: _jsx("div", { dangerouslySetInnerHTML: {
                                                            __html: recentCommentFromApprover.extraData?.translatedText ||
                                                                recentCommentFromApprover.message,
                                                        } }) })), !compact && _jsx("hr", { className: 'my-1' })] })), isProofNeeded && (_jsx("div", { className: styles.comment, children: _jsx("div", { className: 'flex gap-2', children: _jsx("div", { children: getDocumentDeadlineMessage() }) }) }))] })] })] }), ((isProofNeeded && withinDocumentDeadline) || isInfoNeeded || isRejected) && (_jsxs("div", { className: styles.cta, children: [_jsx(TrackableButton, { enText: isProofNeeded ? t('Upload documents') : isInfoNeeded ? t('Review') : t('Dismiss'), tag: 'notification_cta', appearance: 'primary', fullWidth: true, onClick: handleActionClick, children: _jsxs("div", { className: styles.ctaBtn, children: [isProofNeeded && (_jsxs(_Fragment, { children: [t('Upload documents'), " ", _jsx(ArrowForwardIcon, { size: 24 })] })), isInfoNeeded && (_jsxs(_Fragment, { children: [t('Review'), " ", _jsx(ArrowForwardIcon, { size: 24 })] })), isRejected && (_jsxs(_Fragment, { children: [t('Dismiss'), " ", _jsx(CloseIcon, { size: 24 })] }))] }) }), showReviewDialog && (_jsx(InfoRequestedDialog, { activity: activity, onClose: () => {
                                setShowReviewDialog(false);
                                onReviewed();
                            }, approverComment: recentCommentFromApprover, hideDetailsLink: compact && isProgram }))] }))] }) }));
};
